'use client';

import React, { createContext, useContext, useCallback, ReactNode, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { batch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  setActiveAgent,
  setActiveSession,
  setTypingTitle,
  selectActiveSessionId,
  selectActiveSessionAgentId,
  selectTypingTitle,
} from '@/redux/features/command';

interface CommandSessionContextType {
  agentId: string | null;
  activeSessionId: string | null;
  activeSessionAgentId: string | null;
  typingTitle: { sessionId: string; title: string } | null;
  promptLibraryActiveCategory: string;
  setPromptLibraryActiveCategory: Dispatch<SetStateAction<string>>;
  handleSessionSelect: (sessionId: string | null, agentId?: string | null) => void;
  handleNewSessionCreated: (sessionId: string, agentId?: string | null) => void;
  handleSessionTitleUpdate: (sessionId: string, title: string) => void;
}

const CommandSessionContext = createContext<CommandSessionContextType | undefined>(undefined);

export function CommandSessionProvider({
  agentId,
  sessionIdFromUrl,
  children,
}: {
  agentId: string | null;
  /** When present, applied in the same synchronous batch as `setActiveAgent` so it is not wiped by the agent reducer clearing session. */
  sessionIdFromUrl?: string | null;
  children: ReactNode;
}) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const activeSessionId = useAppSelector(selectActiveSessionId);
  const activeSessionAgentId = useAppSelector(selectActiveSessionAgentId);
  const typingTitle = useAppSelector(selectTypingTitle);
  const [promptLibraryActiveCategory, setPromptLibraryActiveCategory] = useState('Favourites');
  const prevAgentIdRef = useRef<string | null>(null);
  const skipUrlSessionRestoreRef = useRef(false);
  const sessionIdFromUrlRef = useRef(sessionIdFromUrl);
  sessionIdFromUrlRef.current = sessionIdFromUrl;

  useLayoutEffect(() => {
    const prevAgentId = prevAgentIdRef.current;
    const agentChanged = prevAgentId !== null && prevAgentId !== agentId;
    prevAgentIdRef.current = agentId;

    if (agentChanged && !sessionIdFromUrl) {
      skipUrlSessionRestoreRef.current = true;
    } else if (!sessionIdFromUrl) {
      skipUrlSessionRestoreRef.current = false;
    }

    batch(() => {
      dispatch(setActiveAgent(agentId));
      // Only sync session from URL when on an agent chat page (sessionIdFromUrl is
      // explicitly provided). On non-chat pages (prompts, agents, files) the layout
      // passes undefined so we don't wipe the active session just because there's no
      // session_id in those URLs.
      if (sessionIdFromUrl !== undefined) {
        dispatch(
          setActiveSession({
            sessionId: sessionIdFromUrl
              ? sessionIdFromUrl
              : agentChanged
                ? null
                : (sessionIdFromUrl ?? null),
          })
        );
      }
    });
  }, [agentId, sessionIdFromUrl, dispatch]);

  // Re-apply URL session if another effect (e.g. auth hydration) cleared Redux state.
  useLayoutEffect(() => {
    if (skipUrlSessionRestoreRef.current) {
      if (sessionIdFromUrl && activeSessionId === sessionIdFromUrl) {
        skipUrlSessionRestoreRef.current = false;
      }
      return;
    }
    if (!sessionIdFromUrl || activeSessionId === sessionIdFromUrl) return;
    dispatch(setActiveSession({ sessionId: sessionIdFromUrl }));
  }, [sessionIdFromUrl, activeSessionId, dispatch]);

  const handleSessionSelect = useCallback(
    (sessionId: string | null, sessionAgentId?: string | null) => {
      const urlSession = sessionIdFromUrlRef.current;
      if (sessionId && urlSession && sessionId !== urlSession) {
        skipUrlSessionRestoreRef.current = true;
      } else if (!sessionId || sessionId === urlSession) {
        skipUrlSessionRestoreRef.current = false;
      }
      dispatch(setActiveSession({ sessionId, agentId: sessionAgentId ?? undefined }));
      if (!sessionId) {
        setPromptLibraryActiveCategory('Favourites');
      }
    },
    [dispatch]
  );

  const handleNewSessionCreated = useCallback(
    (sessionId: string, sessionAgentId?: string | null) => {
      dispatch(
        setActiveSession({
          sessionId,
          agentId: sessionAgentId ?? agentId ?? undefined,
        })
      );
      setPromptLibraryActiveCategory('Favourites');
    },
    [dispatch, agentId]
  );

  const handleSessionTitleUpdate = useCallback(
    (sessionId: string, title: string) => {
      queryClient.setQueryData(
        ['command-sessions', agentId],
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              items: page.items.map((s: any) => (s.id === sessionId ? { ...s, title } : s)),
            })),
          };
        }
      );
      dispatch(setTypingTitle({ sessionId, title }));
      const duration = title.length * 30 + 500;
      setTimeout(() => dispatch(setTypingTitle(null)), duration);
    },
    [dispatch, queryClient, agentId]
  );

  return (
    <CommandSessionContext.Provider
      value={{
        agentId,
        activeSessionId,
        activeSessionAgentId,
        typingTitle,
        promptLibraryActiveCategory,
        setPromptLibraryActiveCategory,
        handleSessionSelect,
        handleNewSessionCreated,
        handleSessionTitleUpdate,
      }}
    >
      {children}
    </CommandSessionContext.Provider>
  );
}

export function useCommandSession() {
  const context = useContext(CommandSessionContext);
  if (context === undefined) {
    throw new Error('useCommandSession must be used within a CommandSessionProvider');
  }
  return context;
}
